import { useEffect, useState } from "react";
import { Loader2, Save, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_FEMALE_HOME, femaleHomeStatus } from "@/hooks/useFemaleHome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-xl border border-purple-200 bg-white px-4 py-3 text-sm text-[#1E1B4B] focus:outline-none focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent";

export function FemaleHomePanel({ token, onUnauthorized }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/settings/female-home`)
      .then((r) => r.json())
      .then((d) => setForm({ ...DEFAULT_FEMALE_HOME, ...d }))
      .catch(() => setForm(DEFAULT_FEMALE_HOME));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/settings/female-home`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) throw new Error();
      setForm({ ...DEFAULT_FEMALE_HOME, ...(await res.json()) });
      toast.success("Female Home details published", { description: "The Homes and Home pages are updated for every visitor." });
    } catch {
      toast.error("Could not save Female Home details");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#5B21B6]" /></div>;

  const status = femaleHomeStatus(form);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="grid lg:grid-cols-5 gap-8" data-testid="admin-female-home-panel">
      <form onSubmit={save} className="lg:col-span-3 bg-white rounded-3xl border border-purple-100 shadow-sm p-7 sm:p-9 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#F3E8FF] flex items-center justify-center"><Megaphone size={20} className="text-[#5B21B6]" /></div>
          <div>
            <h2 className="font-display font-bold text-xl text-[#2E1065]">Female Home Announcement</h2>
            <p className="text-xs text-slate-500">Publish the location and opening date the moment it's confirmed.</p>
          </div>
        </div>

        <label className="flex items-start gap-3 rounded-2xl border border-purple-100 p-4 cursor-pointer hover:bg-[#FAF8F5]">
          <input type="checkbox" data-testid="fh-announced-checkbox" checked={form.announced} onChange={set("announced")} className="mt-1 accent-[#5B21B6] w-4 h-4" />
          <span>
            <span className="block text-sm font-bold text-[#2E1065]">Announce the Female Home publicly</span>
            <span className="block text-xs text-slate-500">Replaces "Coming Soon" with the location and opening date below.</span>
          </span>
        </label>

        <label className="flex items-start gap-3 rounded-2xl border border-purple-100 p-4 cursor-pointer hover:bg-[#FAF8F5]">
          <input type="checkbox" data-testid="fh-accepting-checkbox" checked={form.accepting} onChange={set("accepting")} className="mt-1 accent-[#5B21B6] w-4 h-4" />
          <span>
            <span className="block text-sm font-bold text-[#2E1065]">Now accepting residents</span>
            <span className="block text-xs text-slate-500">Shows the green "Now Accepting Residents" badge, like the Male Manor.</span>
          </span>
        </label>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Location</label>
          <input data-testid="fh-location-input" value={form.location} onChange={set("location")} className={inputCls} placeholder="e.g. Richmond, VA  or  123 Hope Ln, Richmond, VA 23220" maxLength={200} />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Opening date</label>
          <input data-testid="fh-opening-date-input" value={form.opening_date} onChange={set("opening_date")} className={inputCls} placeholder="e.g. March 2027  or  Spring 2027" maxLength={80} />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Note to visitors (optional)</label>
          <textarea data-testid="fh-note-input" rows={3} value={form.note} onChange={set("note")} className={inputCls} placeholder="e.g. Interest list is open — referrals submitted now will be contacted first." maxLength={600} />
        </div>
        <button type="submit" disabled={saving} data-testid="fh-save-btn"
          className="inline-flex items-center gap-2 bg-[#5B21B6] hover:bg-[#2E1065] disabled:opacity-60 text-white font-bold px-7 py-3.5 rounded-full shadow-lg shadow-purple-500/25 transition-colors">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Publish changes
        </button>
      </form>

      <div className="lg:col-span-2">
        <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Live preview</p>
        <div className="bg-gradient-to-br from-[#5B21B6] to-[#2E1065] rounded-3xl p-7 text-white grain relative" data-testid="fh-preview-card">
          <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-3 ${status.tone === "live" ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-700"}`} data-testid="fh-preview-status">
            {status.label}
          </span>
          <h3 className="font-display font-bold text-2xl">The Female Home</h3>
          <p className="text-sm text-purple-200 mt-1" data-testid="fh-preview-location">
            {form.announced && form.location ? form.location : "Location to be announced, Virginia"}
          </p>
          {form.announced && form.note && <p className="mt-3 text-sm text-purple-100 leading-relaxed">{form.note}</p>}
        </div>
      </div>
    </div>
  );
}
